import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        solid:
          "bg-blue-1 text-white-primary hover:bg-secondary-900 active:bg-secondary-700 focus-visible:ring-primary-500 disabled:bg-secondary-400 disabled:text-secondary-600",
        outlined:
          "border border-blue-1 bg-transparent text-blue-1 hover:bg-secondary-100 active:bg-secondary-200 active:border-secondary-700 active:text-secondary-700 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:border-secondary-400 disabled:text-secondary-500 disabled:bg-transparent",
        ghost:
          "bg-transparent text-blue-1 hover:bg-secondary-100 active:bg-secondary-200 active:text-secondary-700 focus-visible:ring-primary-500 focus-visible:bg-secondary-200 disabled:text-secondary-500 disabled:bg-transparent",
        pill: "rounded-full border border-blue-1 bg-transparent text-blue-1 hover:bg-secondary-100 active:bg-secondary-200 active:border-secondary-700 active:text-secondary-700 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:border-secondary-400 disabled:text-secondary-500 disabled:bg-transparent",
        anchor:
          "bg-transparent text-secondary-700 underline-offset-4 hover:underline active:text-blue-1 focus-visible:ring-primary-500 disabled:text-secondary-500 disabled:no-underline p-0 h-auto rounded-none",
      },
      size: {
        default: "h-10 px-6 py-2 text-sm",
        xl: "h-12 px-8 py-3 text-base",
        compact: "h-8 px-4 py-1 text-xs",
      },
    },
    defaultVariants: {
      variant: "solid",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "anchor",
        size: ["default", "xl", "compact"],
        class: "h-auto px-0 py-0",
      },
    ],
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const spinnerSizes = { compact: "h-3 w-3", default: "h-4 w-4", xl: "h-5 w-5" };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const resolvedLeftIcon = loading ? (
      <Loader2
        className={cn(spinnerSizes[size ?? "default"], "animate-spin")}
      />
    ) : (
      leftIcon
    );
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {resolvedLeftIcon}
        {children}
        {rightIcon}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
